import sanitize from "sanitize-html";
import { isString } from "lodash";
import { routes } from "../routes";
import { safetyUrl, sanitizeHtmlAllowedTags } from "./constants";

export const checkTranslationTags = text => {
	const __html = sanitize(text, {
		allowedTags: sanitizeHtmlAllowedTags,
	});

	return {
		__html,
	};
};

export const sanitizePostContent = content => {
	const __html = sanitize(content, {
		allowedTags: [...sanitize.defaults.allowedTags, `img`],
		allowedAttributes: {
			a: [`href`, `rel`, `target`],
			img: [`src`, `class`, `alt`, `data-pagespeed-url-hash`, `onload`],
			figure: [`class`],
			h1: [`id`, `class`],
			h2: [`id`, `class`],
			h3: [`id`, `class`],
			h4: [`id`, `class`],
			h5: [`id`, `class`],
			h6: [`id`, `class`],
		},
		transformTags: {
			a: (tagName, attribs) => {
				const { href } = attribs;
				let newAttribs = { ...attribs };
				const isSafe = href.slice(0, 22).includes(safetyUrl);

				if (!isSafe) {
					newAttribs = { ...attribs, rel: "noopener noreferrer nofollow", target: "_blank" };
				}

				if (isSafe) {
					newAttribs = { ...attribs, rel: "noopener noreferrer dofollow", target: "_blank" };
				}

				return {
					tagName,
					attribs: newAttribs,
				};
			},
		},
	});

	return {
		__html,
	};
};

export const readingTime = (time, lang) => {
	switch (lang) {
		case "pl": {
			if (time === 1) return `${time} minuta`;
			if (time === 12) return `${time} minut`;
			if (time === 13) return `${time} minut`;
			if (time === 14) return `${time} minut`;
			if (time.toString().includes(String(2)) && !time.toString().includes(String(0)))
				return `${time} minuty`;
			if (time.toString().includes(String(3)) && !time.toString().includes(String(0)))
				return `${time} minuty`;
			if (time.toString().includes(String(4)) && !time.toString().includes(String(0)))
				return `${time} minuty`;

			return `${time} minut`;
		}
		case "en":
		default: {
			if (time === 1) return `${time} minute`;
			return `${time} minutes`;
		}
	}
};

export const generateTagUrl = tagName =>
	isString(tagName) && tagName.toLowerCase().replace(" ", "-");
export const scrollToTop = () => {
	if (!window) return;

	return window.scrollTo({ top: 0, behavior: "smooth" });
};

/**
 *
 * @param {Object} object could look like this { toolTrackingFunction: mixpanel.track, tagManager: bool, eventName: string }
 */

export const trackUser = ({ toolTrackingFunction, tagManager, eventName }) => {
	toolTrackingFunction?.(eventName);

	tagManager && window?.dataLayer?.push?.({ event: eventName });
};

export const getLanguage = () => {
	const lang = window.navigator.userLanguage || window.navigator.language;
	const formattedLang = lang?.substr(0, 2);

	switch (formattedLang) {
		case "pl":
			return "pl";
		default:
			return "";
	}
};

export const getPathName = (path, locale) => {
	if (!path) throw Error("Path is required to run this function!");
	path =
		path.length > 1 && path.lastIndexOf("/") > path.length - 2
			? path.slice(0, path.length - 1)
			: path;

	return findRouteMatch(path, locale);
};

const findRouteMatch = (route, locale = "en") => {
	const currentLocale = locale === "en" ? "pl" : "en";

	const routesValues = Object.values(routes[currentLocale]);
	const routeValueIndex = routesValues.indexOf(route);
	const routeKey = Object.keys(routes[currentLocale])[routeValueIndex];
	const routeMatch = routes[locale][routeKey];

	if (routeMatch) {
		return routeMatch;
	}

	if (
		route.length > 10 &&
		route.includes("/blog/") &&
		!route.includes("/blog/tag/") &&
		!route.includes("/blog/strona/") &&
		!route.includes("/blog/page/")
	) {
		if (locale === "en") return "/blog";

		return "/pl/blog";
	}

	if (locale === "en") {
		return route.slice(3, route.length);
	}

	return `/pl${route}`;
};

export const isServer = () => {
	return typeof window === "undefined";
};

export const getPreferedLanguage = () => {
	if (isServer()) return null;

	const preferredLanguage = window.localStorage.getItem("prefered_language");

	return preferredLanguage;
};

export const setPreferedLanguage = language => {
	if (!language || typeof language !== "string") throw new Error("You have to provide language!");
	if (isServer()) return null;

	window.localStorage.setItem("prefered_language", language);
};
